import { useEffect, useRef } from 'react';
import { log as logger } from '../lib/log';

/**
 * Highlight.io Client-Side Initialization with Robust LaunchDarkly Integration
 *
 * Environment Variables Required:
 * - NEXT_PUBLIC_HIGHLIGHT_ENABLED=true  (must be explicitly enabled)
 * - NEXT_PUBLIC_HIGHLIGHT_PROJECT_ID    (your Highlight.io project ID)
 *
 * This component implements all concrete fixes for LaunchDarkly plugin warnings:
 * 1. Hardened ordering + validation: Waits for valid session data before LD integration
 * 2. Namespacing & env consistency: Clears stale session data from previous project IDs
 * 3. Single initialization & retries: Uses Promise-based retry mechanism (max 2.5s)
 * 4. Guard dev/test: Skips LD attachment in non-production environments
 *
 * The session validation requires sessionSecureID to be a non-empty string and optionally
 * validates sessionURL and project ID consistency.
 */

/**
 * Highlight.io initialization component with LaunchDarkly session integration
 */
export const HighlightInit: React.FC = () => {
  const didInit = useRef(false);

  // Read env vars once at component level (stable values)
  const enabled = process.env.NEXT_PUBLIC_HIGHLIGHT_ENABLED === 'true';
  const projectId = process.env.NEXT_PUBLIC_HIGHLIGHT_PROJECT_ID;
  const isProduction = process.env.NODE_ENV === 'production';

  // Check if Highlight should be enabled (computed once)
  const shouldEnable = isProduction && enabled && !!projectId;

  useEffect(() => {
    // Only initialize on client-side
    if (typeof window === 'undefined') return;

    // If disabled, log ONCE then bail
    if (!shouldEnable) {
      if (!didInit.current) {
        didInit.current = true;
        // Only log in development
        if (process.env.NODE_ENV !== 'production') {
          logger.info('[HIGHLIGHT] disabled – set NEXT_PUBLIC_HIGHLIGHT_ENABLED=true and NEXT_PUBLIC_HIGHLIGHT_PROJECT_ID to enable');
        }
      }
      return;
    }

    // Already initialized, bail out
    if (didInit.current) return;
    didInit.current = true;

    const initHighlight = async () => {
      try {
        const { H } = await import('@highlight-run/next/client');

        // Initialize Highlight first
        H.init(projectId!, {
          serviceName: 'next-web',
          tracingOrigins: true,
          networkRecording: {
            enabled: true,
            recordHeadersAndBody: true,
            // Redact sensitive headers
            headerKeysToRecord: [
              'content-type',
              'x-requested-with',
              'cache-control',
              'pragma',
              // Exclude sensitive headers
              '!authorization',
              '!cookie',
              '!x-api-key',
              '!stripe-signature',
              '!paypal-transmission-sig'
            ]
          },
          environment: process.env.VERCEL_ENV || 'development'
        });

        // Clear any stale session data from previous project IDs
        clearStaleSessionData(projectId);

        // Wait for Highlight session to be fully created before attempting LD integration
        const waitForValidSession = () => {
          return new Promise<HighlightSessionData | null>((resolve) => {
            let attempts = 0;
            const maxAttempts = 10; // Max 2.5 seconds (10 * 250ms)
            const checkInterval = 250;

            const checkSession = () => {
              attempts++;
              const sessionData = safeGetHighlightSession();

              if (sessionData && validateSessionData(sessionData)) {
                resolve(sessionData);
              } else if (attempts >= maxAttempts) {
                logger.debug('[HIGHLIGHT] Session data not available after max attempts');
                resolve(null);
              } else {
                setTimeout(checkSession, checkInterval);
              }
            };

            // Start checking immediately
            checkSession();
          });
        };

        // Wait for valid session data, then attach to LaunchDarkly
        waitForValidSession().then((sessionData) => {
          if (sessionData) {
            attachLdHighlightSession(sessionData);
            logger.info('[HIGHLIGHT] LaunchDarkly session attached successfully');
          } else {
            logger.debug('[HIGHLIGHT] LaunchDarkly session attachment skipped - no valid session data available');
          }
        });

        logger.info('[HIGHLIGHT] Client initialized successfully');
      } catch (error) {
        logger.warn('[HIGHLIGHT] Failed to initialize client', error);
      }
    };

    initHighlight();
  }, [shouldEnable, projectId]); // Stable dependencies

  // This component doesn't render anything
  return null;
};

/**
 * Clear stale session data from previous project IDs to prevent LD plugin conflicts
 */
function clearStaleSessionData(currentProjectId: string): void {
  if (typeof window === 'undefined') return;

  try {
    // Look for any sessionData_* keys that don't match our current project ID
    const keysToRemove: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('sessionData_') && !key.includes(currentProjectId)) {
        keysToRemove.push(key);
      }
    }

    // Also check sessionStorage for highlightSession from different projects
    const highlightSessionRaw = sessionStorage.getItem('highlightSession');
    if (highlightSessionRaw) {
      try {
        const sessionData = JSON.parse(highlightSessionRaw);
        if (sessionData && sessionData.projectID && sessionData.projectID !== currentProjectId) {
          sessionStorage.removeItem('highlightSession');
          logger.debug('[HIGHLIGHT] Cleared stale session data from previous project ID');
        }
      } catch (error) {
        // If we can't parse it, it's likely corrupted anyway
        sessionStorage.removeItem('highlightSession');
      }
    }

    // Remove mismatched localStorage keys
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
      logger.debug(`[HIGHLIGHT] Cleared stale localStorage key: ${key}`);
    });
  } catch (error) {
    logger.debug('[HIGHLIGHT] Error clearing stale session data', error);
  }
}

/**
 * Strict validation of session data to ensure it's usable by LaunchDarkly
 */
function validateSessionData(data: any): boolean {
  if (!data || typeof data !== 'object') return false;

  // Must have sessionSecureID as a non-empty string
  if (typeof data.sessionSecureID !== 'string' || data.sessionSecureID.trim() === '') {
    return false;
  }

  // Optional but if present, sessionURL should be a string
  if (data.sessionURL && typeof data.sessionURL !== 'string') {
    return false;
  }

  // Ensure project ID matches (if present in session data)
  const currentProjectId = process.env.NEXT_PUBLIC_HIGHLIGHT_PROJECT_ID;
  if (data.projectID && data.projectID !== currentProjectId) {
    return false;
  }

  return true;
}

/**
 * Safely retrieve and validate Highlight session data from sessionStorage
 */
function safeGetHighlightSession(): HighlightSessionData | null {
  try {
    const raw = sessionStorage.getItem('highlightSession');
    if (!raw) return null;

    const data = JSON.parse(raw);

    // Use strict validation
    if (!validateSessionData(data)) {
      logger.debug('[HIGHLIGHT] Session data failed validation');
      return null;
    }

    return data as HighlightSessionData;
  } catch (error) {
    logger.debug('[HIGHLIGHT] Failed to parse session data', error);
    return null;
  }
}

/**
 * Attach Highlight session data to LaunchDarkly user context
 */
function attachLdHighlightSession(sessionData: HighlightSessionData): void {
  if (!sessionData) return;

  // Skip LD attachment in non-production environments or when disabled
  if (process.env.NODE_ENV !== 'production' || process.env.NEXT_PUBLIC_HIGHLIGHT_ENABLED !== 'true') {
    logger.debug('[HIGHLIGHT] Skipping LaunchDarkly attachment - not in production or Highlight disabled');
    return;
  }

  const { sessionSecureID, sessionURL } = sessionData;

  // Create attributes object with available session data
  const attributes: Record<string, string> = {};
  if (sessionSecureID) attributes.highlightSessionId = sessionSecureID;
  if (sessionURL) attributes.highlightSessionUrl = sessionURL;

  // If no attributes to send, skip
  if (Object.keys(attributes).length === 0) {
    logger.debug('[HIGHLIGHT] No valid session data to attach to LaunchDarkly');
    return;
  }

  try {
    // Check if LaunchDarkly client is available and properly initialized
    if (typeof window !== 'undefined' && (window as any).LDClient) {
      const ldClient = (window as any).LDClient;

      // Ensure LD client is ready before trying to identify
      if (typeof ldClient.identify === 'function') {
        // Identify user with Highlight session attributes
        // This creates a context that includes the Highlight session data
        ldClient.identify({
          kind: 'user',
          key: `highlight-${sessionSecureID || 'anonymous'}`,
          attributes: attributes
        });

        logger.debug('[HIGHLIGHT] LaunchDarkly context updated with session data');
      } else {
        logger.debug('[HIGHLIGHT] LaunchDarkly client identify method not available');
      }
    } else {
      logger.debug('[HIGHLIGHT] LaunchDarkly client not available, skipping session attachment');
    }
  } catch (error) {
    logger.warn('[HIGHLIGHT] Failed to attach session to LaunchDarkly', error);
  }
}

/**
 * Type definition for Highlight session data
 */
interface HighlightSessionData {
  sessionSecureID?: string;
  sessionURL?: string;
  [key: string]: any;
}
